import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { post, get } from "../../services/ApiService";
import { Button, Card, Divider } from "antd";
import Layout from "../../components/Layout";
import Loader from "../../components/Loader";
import InquiryActionModal from "./InquiryActionModal";
import Breadcrumbs from "../../components/Breadcrumbs";
import InquiryDetailHeader from "./components/InquiryDetailHeader";
import InquiryProduct from "./components/InquiryProduct";
import InquiryHistory from "./components/InquiryHistory";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import toast, { Toaster } from "react-hot-toast";
import {
  TruckOutlined,
  MessageOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import "./index.css";

import ChatHistory from "../Orders/Assessment/ChatHistory";
import OrdersHistory from "../Orders/Assessment/OrdersHistory";

const InquiryDetail = () => {
  const { id } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ticket, setTicket] = useState(null);
  const canViewMsg = usePermissionCheck(["order.view_message"]);
  const canSendMsg = usePermissionCheck(["order.send_message"]);
  const [hasUnreadMessage, setHasUnreadMessage] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(true);
  const [isNoMessage, setNoMessage] = useState(false);
  const [NoMessageText, setNoMessageText] = useState("");
  const textareaRef = useRef(null);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isSending, setIsSending] = useState(false);
    const [groupedOrders, setGroupedOrders] = useState({});

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);
  // Fetch Inquiry
  useEffect(() => {
    const fetchInquiry = async () => {
      try {
        setIsChatLoading(true);
        const res = await get(`api/inquiries/${id}`);
        setInquiry(res.data);
        setOrder(res.order);
        setMessages(res.messages || []);
        setTicket(res.ticket);
        setFaqs(res.faqs || []);
        setHasUnreadMessage(res.hasUnreadMessage);
        const orders = res.orders;
        if (orders.length > 0) {
          const grouped = groupOrdersByMonth(orders);
          setGroupedOrders(grouped);
        }
      } catch (err) {
        console.error("Failed to fetch inquiry:", err);
      } finally {
        setLoading(false);
        setIsChatLoading(false);
      }
    };
    fetchInquiry();
  }, [id]);

  const handleSendMessage = async () => {
    if (!message && message === "") {
      toast.error("Enter your message first");
      return;
    }
    const formData = new FormData();
    formData.append("message", message);
    formData.append("orderId", order?._id);
    attachments.forEach((file, index) => {
      formData.append("files", file);
    });
    setIsSending(true);
    const res = await post(`api/messages/send`, formData, true);
    setIsSending(false);
    if (res.success) {
      toast.success("Message sent");
      const newMessage = {
        body: message,
        creationDate: new Date().toISOString(),
        messageID: "",
        sender: "pillpharm",
        subject: "",
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      setAttachments([]);
      setMessage("");
    }
  };

  // Global Event Listeners
  useEffect(() => {
    const handleEvent = (type) => (e) => {
      setModalAction({ type, id: e.detail.id });
      setIsModalVisible(true);
    };

    window.addEventListener("provide_shipment_info", handleEvent("shipment"));
    window.addEventListener("send_message", handleEvent("message"));
    window.addEventListener("issue_refund", handleEvent("refund"));
    window.addEventListener("no_response_needed", handleEvent("close"));

    return () => {
      window.removeEventListener( "provide_shipment_info", handleEvent("shipment"));
      window.removeEventListener("send_message", handleEvent("message"));
      window.removeEventListener("no_response_needed", handleEvent("close"));
    };
  }, []);

  const groupOrdersByMonth = (orders) => {
    const groupedOrders = {};
    orders.forEach((order) => {
      const date = new Date(order.createdAt);
      const month = date.toLocaleString("default", { month: "long" });
      const year = date.getFullYear();
      const monthYear = `${month} ${year}`;

      if (!groupedOrders[monthYear]) {
        groupedOrders[monthYear] = [];
      }
      groupedOrders[monthYear].push(order);
    });

    return groupedOrders;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center mt-5">
          <Loader />
        </div>
      </Layout>
    );
  }

  if (!inquiry) {
    return (
      <Layout>
        <div className="text-center mt-5">
          <h4>No inquiry found.</h4>
        </div>
      </Layout>
    );
  }

  const {
    itemDetails,
    claimAmount,
    expiryDate,
    buyer,
    inquiryQuantity,
    inquiryId,
    status,
    inquiryHistory,
    shipmentTracking,
    buyerRequested,
  } = inquiry;

  const handleAction = (type) => {
    window.dispatchEvent(new CustomEvent(type, { detail: { id: id } }));
  };

  const handleCloseTicket = (ticket) => {
    setTicket(ticket);
  };

  return (
    <Layout>
      <div className="mb-5">
        <h2 className="fs-4 mb-0">Case Detail</h2>
        <Breadcrumbs />
      </div>
      <InquiryDetailHeader
        inquiry={inquiry}
        order={order}
        shipmentTracking={shipmentTracking}
      />
      <div className="row">
        <div className="col-lg-5 col-md-6 col-12 mt-4">
          <OrdersHistory groupedOrders={groupedOrders} />
          <InquiryProduct
            product={itemDetails}
            inquiryQuantity={inquiryQuantity}
            claimAmount={claimAmount}
            expiryDate={expiryDate}
            status={status}
            buyerRequested={buyerRequested}
            buyer={buyer} 
            inquiryId={inquiryId}
          />
          <Divider />
          <InquiryHistory inquiryHistory={inquiryHistory} />
          <div className="custom-action-panel mt-4">
            <div className="action-card shipment" onClick={() => handleAction("provide_shipment_info")}>
              <TruckOutlined className="icon" />
              <span className="action-title">SHIPMENT INFO</span>
            </div>
            <div className="action-card message" onClick={() => handleAction("send_message")}>
              <MessageOutlined className="icon" />
              <span className="action-title">SEND MESSAGE</span>
            </div>
            <div className="action-card refund" onClick={() => handleAction("issue_refund")}>
              <ExclamationCircleOutlined className="icon" />
              <span className="action-title">ISSUE REFUND</span>
            </div>
            <div className="action-card close" onClick={() => handleAction("no_response_needed")}>
              <CheckCircleOutlined className="icon" />
              <span className="action-title">NO RESPONSE NEEDED </span>
            </div>

          </div>

        </div>
        <div className="col-lg-7 col-md-6 col-12 mt-4">
          <ChatHistory
            canViewMsg={canViewMsg}
            canSendMsg={canSendMsg}
            messages={messages}
            isChatLoading={isChatLoading}
            isNoMessage={isNoMessage}
            NoMessageText={NoMessageText}
            textareaRef={textareaRef}
            message={message}
            setMessage={setMessage}
            attachments={attachments}
            setAttachments={setAttachments}
            faqs={faqs}
            setFaqs={setFaqs}
            handleSendMessage={handleSendMessage}
            isSending={isSending}
            order={order}
            questions={questions}
            ticket={ticket}
            handleCloseTicket={handleCloseTicket}
            hasUnreadMessage={hasUnreadMessage}
            setHasUnreadMessage={setHasUnreadMessage}
          />



        </div>
      </div>

      <InquiryActionModal
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        actionType={modalAction?.type}
        id={modalAction?.id}
        onSuccess={() => window.location.reload()}
      />
    </Layout>
  );
};

export default InquiryDetail;
